import Component from "@glimmer/component";
import ENV from "glesys-controlpanel/config/environment";
import { task, restartableTask } from "ember-concurrency";
import PageContent from "glesys-controlpanel/components/page-content";
import PageContentServiceIntro from "glesys-controlpanel/components/page-content/service-intro";
import PageFooter from "glesys-controlpanel/components/page-footer";
import GButton from "glesys-controlpanel/components/g-button";
import Box from "glesys-controlpanel/components/box";
import { t } from "ember-intl";
import Select, { Option } from "glesys-controlpanel/components/select";
import { tracked } from "@glimmer/tracking";
import canManage from "glesys-controlpanel/helpers/can-manage";
import TaskForm from "glesys-controlpanel/components/task-form";
import { service } from "@ember/service";
import DataLoader from "glesys-controlpanel/components/data-loader";
import GInput from "glesys-controlpanel/components/g-input";
import Label from "glesys-controlpanel/components/label";
import { on } from "@ember/modifier";
import Cost from "glesys-controlpanel/components/cost";
import IconsLoadingSpinner from "glesys-controlpanel/components/icons/loading-spinner";
import SelectEngine from "./components/select-engine";
import SelectPlan from "./components/select-plan";
import engines from "./engines";
import { saveRecord } from "@warp-drive/legacy/compat/builders";

export default class extends Component {
  @service store;
  @service router;
  @service http;
  @service intl;
  @service abilities;

  @tracked selectedEngine;

  @tracked plans;
  @tracked selectedPlan;

  @tracked selectedDataCenter = { id: "dc-fbg1" };
  @tracked name = "";
  @tracked firewall = "";

  get engines() {
    return engines.all(this.intl);
  }

  constructor() {
    super(...arguments);
    this.selectedEngine = this.engines.at(0);
  }

  get dataCenters() {
    return this.store.peekAll("data-center").filter((dataCenter) => dataCenter.hasDatabaseSupport);
  }

  get project() {
    return this.args.model.project;
  }

  get groupedPlans() {
    let grouped = Object.groupBy(this.plans, ({ cpucores, memoryingib }) => `${cpucores}-${memoryingib}`);
    return Object.keys(grouped).flatMap((key) => {
      let plans = grouped[key];
      let plan = plans.at(0);
      return {
        key,
        cpucores: plan.cpucores,
        memoryingib: plan.memoryingib,
        selected: plans.at(0),
        storage: plans,
      };
    });
  }

  get formIsInValid() {
    return !(this.selectedEngine && this.selectedPlan && this.selectedDataCenter && this.name);
  }

  selectEngine = (engine) => (this.selectedEngine = engine);

  selectPlan = (plan) => {
    this.selectedPlan = plan;
    this.estimatedCost.perform();
  };

  selectDataCenter = (event) =>
    (this.selectedDataCenter = this.dataCenters.find((dataCenter) => dataCenter.id === event.target.value));

  onNameChange = (event) => (this.name = event.target.value);

  onFirewallChange = (event) => (this.firewall = event.target.value);

  loadPlans = task(async () => {
    let response = await this.http.request({ url: `${ENV.apiBaseUri}/database/listplans` });

    this.plans = response.content.plans;

    this.selectPlan(this.groupedPlans.at(0));
  });

  estimatedCost = restartableTask(async () => {
    if (this.abilities.cannot("access pricing for organization", this.project.organization)) {
      return { estimated: { price: null, total: null }, currency: null };
    }

    let response = await this.http.request({
      url: `${ENV.apiBaseUri}/database/estimatedcost?projectkey=${this.project.id}&plankey=${this.selectedPlan.selected.key}`,
      method: "POST",
    });

    return response.content.billing;
  });

  createDatabaseTask = task(async () => {
    let database = this.store.createRecord("database", {
      project: this.project,
      engine: this.selectedEngine.label.toLowerCase(),
      engineversion: this.selectedEngine.selected.value,
      plankey: this.selectedPlan.selected.key,
      datacenterkey: this.selectedDataCenter.id,
      allowlist: this.firewall.split(/,\s*/).filter(Boolean),
      name: this.name,
    });

    try {
      await this.store.request(saveRecord(database));

      this.router.transitionTo("databases.details", database.id);
    } catch (error) {
      database.unloadRecord();
      throw error;
    }
  });

  <template>
    <PageContent>
      <PageContentServiceIntro @title={{t "databases.create.page-title"}} />
      <TaskForm id="create-database-form" @task={{this.createDatabaseTask}}>
        <div class="grid grid-rows-auto gap-8">
          <SelectEngine @engines={{this.engines}} @selected={{this.selectedEngine}} @onChange={{this.selectEngine}} />

          <DataLoader @load={{this.loadPlans}}>
            <SelectPlan @plans={{this.groupedPlans}} @selected={{this.selectedPlan}} @onChange={{this.selectPlan}} />
          </DataLoader>

          <Box>
            <:header>{{t "label.data-center"}}</:header>
            <:default>
              <Select
                aria-label={{t "label.data-center"}}
                data-test-data-center-select
                @value={{this.selectedDataCenter.id}}
                {{on "change" this.selectDataCenter}}
              >
                {{#each this.dataCenters as |dataCenter|}}
                  <Option value={{dataCenter.id}}>
                    {{dataCenter.name}}
                    ({{dataCenter.id}})
                  </Option>
                {{/each}}
              </Select>
            </:default>
          </Box>

          <Box>
            <:header>{{t "label.firewall"}}</:header>
            <:default>
              <span class="text-gray-500 text-base mb-6">{{t "databases.details.firewall.info"}}</span>
              <GInput
                data-test-database-firewall
                value={{this.firewall}}
                placeholder={{t "component.modal.database-firewall-update-access.input-placeholder"}}
                {{on "input" this.onFirewallChange}}
              />
            </:default>
          </Box>

          <Box>
            <:header>{{t "label.name"}}</:header>
            <:default>
              <Label class="sr-only" for="database-name">{{t "label.name"}}</Label>
              <GInput
                id="database-name"
                data-test-database-name
                @value={{this.name}}
                {{on "input" this.onNameChange}}
              />
            </:default>
          </Box>
        </div>
      </TaskForm>
    </PageContent>
    <PageFooter class="bg-white flex justify-end items-center gap-6 sticky bottom-0 w-full h-auto">
      {{#if this.estimatedCost.isRunning}}
        <IconsLoadingSpinner class="size-6" />
      {{else if this.estimatedCost.last.value.estimated}}
        <Cost
          @price={{this.estimatedCost.last.value.estimated.price}}
          @discountedPrice={{this.estimatedCost.last.value.estimated.total}}
          @currency={{this.estimatedCost.last.value.currency}}
        />
      {{/if}}
      {{#if (canManage)}}
        <GButton
          data-test-database-create-submit
          @disabled={{this.formIsInValid}}
          @loading={{this.createDatabaseTask.isRunning}}
          @type="submit"
          @color="green"
          form="create-database-form"
        >
          {{t "button.create"}}
        </GButton>
      {{/if}}
    </PageFooter>
  </template>
}
